import { inject, Injectable } from '@angular/core';
import { Observable, of, Subject, timer } from 'rxjs';
import {
  catchError,
  concatMap,
  switchMap,
  takeUntil,
  takeWhile
} from 'rxjs/operators';
import { PUSH_CONFIRMATION_WAITING } from '../../../constants';
import {
  InitiateAccountRecovery,
  InitiateAuthentication,
  InitiateSsoSession,
  LoginOAuthParams,
  SelectDevice,
  SubmitSkymilesNumber
} from '../../../models';
import { ContinueAccountRecoveryService } from '../../multi-factor-authentication-adapter-service/continue-account-recovery/continue-account-recovery.service';
import { ContinueAuthenticationService } from '../../multi-factor-authentication-adapter-service/continue-authentication/continue-authentication.service';
import { FlyDeltaPollService } from '../../multi-factor-authentication-adapter-service/fly-delta-poll/fly-delta-poll.service';
import { InitiateAuthenticationService } from '../../multi-factor-authentication-adapter-service/initiate-authentication/initiate-authentication.service';
import { InitiateSelfServicePasswordResetService } from '../../multi-factor-authentication-adapter-service/initiate-self-service-password-reset/initiate-self-service-password-reset.service';
import { InitiateSsoSessionService } from '../../multi-factor-authentication-adapter-service/initiate-sso-session/initiate-sso-session.service';
import { PostSkymilesNumberService } from '../../multi-factor-authentication-adapter-service/post-skymiles-number/post-skymiles-number.service';
import { SelectDeviceService } from '../../multi-factor-authentication-adapter-service/select-device/select-device.service';
import { SubmitEmailOtpService } from '../../multi-factor-authentication-adapter-service/submit-email-otp/submit-email-otp.service';
import { SubmitNewPasswordService } from '../../multi-factor-authentication-adapter-service/submit-new-password/submit-new-password.service';
import { MfaUtilService } from '../../multi-factor-authentication-adapter-service/util-service/mfa-util.service';
import { PostWithFingerPrintService } from '../../remember-me-adapter-service/post-with-fingerprint/post-with-fingerprint-service';

@Injectable({
  providedIn: 'root'
})
export class SelfServicePasswordResetHelperService {
  flowId = '';
  stopPoll$ = new Subject();

  initiateSsoSession = inject(InitiateSsoSessionService);
  postWithFingerPrintService = inject(PostWithFingerPrintService);
  initiateSelfServicePasswordReset = inject(
    InitiateSelfServicePasswordResetService
  );

  postSkymilesNumber = inject(PostSkymilesNumberService);
  initiateAuthenticationService = inject(InitiateAuthenticationService);
  selectDevice = inject(SelectDeviceService);
  submitEmailOtp = inject(SubmitEmailOtpService);
  continueAuthentication = inject(ContinueAuthenticationService);
  submitNewPassword = inject(SubmitNewPasswordService);
  mfaUtils = inject(MfaUtilService);
  flyDeltaPoll = inject(FlyDeltaPollService);
  continueAccountRecovery = inject(ContinueAccountRecoveryService);

  initializeSelfServiceReset(
    loginOAuthParams: LoginOAuthParams
  ): Observable<InitiateAccountRecovery | string> {
    return this.initiateSsoSession
      .initiateSsoSession(loginOAuthParams, 'SELF_SERVICE_RESET_PASSWORD')
      .pipe(
        concatMap((response: InitiateSsoSession) => {
          if (response && response.id) {
            this.flowId = response.id;
          }
          return this.initiateSelfServicePasswordReset.initiateAccountRecovery(
            loginOAuthParams.oauthConfig,
            response.id ? response.id : ''
          );
        }),
        catchError((error) => {
          return of(this.mfaUtils.getMfaRegistrationError(error));
        })
      );
  }

  submitSkymilesNumber(
    loginOAuthParams: LoginOAuthParams,
    skymilesNumber: string
  ): Observable<SubmitSkymilesNumber | string> {
    return this.postSkymilesNumber
      .submitSkymilesNumber(
        loginOAuthParams.oauthConfig,
        this.flowId,
        skymilesNumber
      )
      .pipe(
        catchError((error) => {
          return of(this.mfaUtils.getMfaRegistrationError(error));
        })
      );
  }

  authenticate(
    loginOAuthParams: LoginOAuthParams,
    flowId: string
  ): Observable<string | InitiateAuthentication> {
    this.flowId = flowId;
    if (loginOAuthParams.oauthConfig.MFA_ACTION_SSPR_VALUE) {
      return this.initiateAuthenticationService
        .initiateAuthentication(loginOAuthParams.oauthConfig, flowId)
        .pipe(
          catchError((error) => {
            return of(this.mfaUtils.getMfaRegistrationError(error));
          })
        );
    } else {
      return of('');
    }
  }

  selectDeviceMethod(
    loginOAuthParams: LoginOAuthParams,
    deviceRef: string
  ): Observable<SelectDevice | string> {
    return this.selectDevice
      .selectDevice(loginOAuthParams.oauthConfig, this.flowId, deviceRef)
      .pipe(
        catchError((error) => {
          return of(this.mfaUtils.getMfaRegistrationError(error));
        })
      );
  }

  stopPolling() {
    this.stopPoll$.next();
  }

  initiateFlyDeltaPoll(loginOAuthParams: LoginOAuthParams) {
    const pollTime =
      loginOAuthParams.oauthConfig.FLY_DELTA_POLLING_TIMER ?? 5000;

    const poll$ = timer(0, pollTime).pipe(
      switchMap(() =>
        this.flyDeltaPoll.submitFlyDeltaPoll(
          loginOAuthParams.oauthConfig,
          this.flowId ? this.flowId : ''
        )
      ),
      takeUntil(this.stopPoll$),
      takeWhile((val) => val.status === PUSH_CONFIRMATION_WAITING, true),
      catchError((error) => {
        return of(this.mfaUtils.getMfaRegistrationError(error));
      })
    );
    return poll$;
  }
}
